import { uniqBy } from 'lodash';
import type { Reference, StoreObject } from '@apollo/client';
import type {
  FieldMergeFunction,
  KeySpecifier,
} from '@apollo/client/cache/inmemory/policies';
import { cursorPaginationFieldPolicy } from '../../../../../utils/policies/cursorPaginationFieldPolicy';
import type { TypePolicies } from '../../../../../utils/types/TypePolicies';

const projectCursorPolicy = cursorPaginationFieldPolicy([
  'filter',
  'page',
  ['first'],
  'membershipFilter',
  ['query', 'collectionFilters'],
  'orders',
  '$order',
]);

// the all projects query uses cursor pagination with different filtering options
const ALL_PROJECTS_ALIAS = 'allProjects';

export const typePolicies: TypePolicies = {
  Account: {
    fields: {
      projects: {
        keyArgs: (args, { field }) => {
          if (field?.alias?.value === ALL_PROJECTS_ALIAS) {
            return ['accountId', 'filter', 'orders'];
          }

          return args?.page?.mode === 'OFFSET'
            ? ['page', ['afterOffset'], 'filter']
            : (projectCursorPolicy.keyArgs as KeySpecifier);
        },
        merge: (
          existing = {
            nodes: [],
            pageInfo: {
              endCursor: null,
              hasNextPage: true,
              hasPreviousPage: false,
            },
            totalCount: null,
          },
          incoming,
          options
        ) => {
          if (
            typeof projectCursorPolicy.merge === 'function' &&
            options.field?.alias?.value === ALL_PROJECTS_ALIAS
          ) {
            return projectCursorPolicy.merge(existing, incoming, options);
          }

          if (options?.args?.page?.mode === 'OFFSET') {
            const mergedNodes = existing.nodes ? existing.nodes.slice(0) : [];

            if (incoming?.nodes) {
              const { afterOffset = 0 } = options?.args;
              for (let i = 0; i < incoming.nodes.length; i += 1) {
                const incomingNode = incoming.nodes[i];
                mergedNodes[afterOffset + i] = incomingNode;
              }
            }

            const nodes = uniqBy(mergedNodes, (node) =>
              options.readField('id', node as StoreObject | Reference)
            );

            return {
              ...existing,
              ...incoming,
              nodes,
            };
          }

          return (projectCursorPolicy.merge as FieldMergeFunction)(
            existing,
            incoming,
            options
          );
        },
      },

      fullAccessProjects: cursorPaginationFieldPolicy([
        'accountId',
        'filter',
        'orders',
      ]),
      partialAccessProjects: cursorPaginationFieldPolicy([
        'accountId',
        'filter',
        'orders',
      ]),
    },
  },
};
